import React from "react";
import clsx from "clsx";
import { useTheme } from "@mui/material";
import { MessageText } from "iconsax-react";

import { useTaskComments } from "@SonarCX/providers/TaskCommentsProvider";
import useStyles from "../styles";

type CommentsProps = {
  task: LocalTask;
  showCommentPreview: boolean;
  setShowCommentPreview: React.Dispatch<React.SetStateAction<boolean>>;
  shouldCheckClose?: boolean;
  setShouldCheckClose?: React.Dispatch<React.SetStateAction<boolean>>;
};

const Comments: React.FC<CommentsProps> = ({
  task = {} as LocalTask,
  showCommentPreview,
  setShowCommentPreview,
  shouldCheckClose,
  setShouldCheckClose,
}) => {
  const theme = useTheme();

  const {
    counts,
    hasModalWithComments,
    loadComments,
    setViewingTaskCommentsID,
  } = useTaskComments();

  const hoverTime = React.useRef<NodeJS.Timeout | null>(null);
  const hoverTimeOut = React.useRef<NodeJS.Timeout | null>(null);
  const [startedLoad, setStartedLoad] = React.useState(false);

  const comments = React.useMemo(() => {
    return Math.max(counts[task.id] ?? 0, task?.comments ?? 0);
  }, [counts, task.id, task.comments]);

  const classes = useStyles();

  const handleMouseEnter = React.useCallback(() => {
    if (hasModalWithComments) {
      return;
    }

    clearTimeout(hoverTimeOut.current as NodeJS.Timeout);
    setShouldCheckClose && setShouldCheckClose(false);
    hoverTime.current = setTimeout(() => {
      setShowCommentPreview(true);
      if (!startedLoad) {
        setStartedLoad(true);
        loadComments(task.id);
        setViewingTaskCommentsID(task.id);
      }
    }, 500);
  }, [
    hasModalWithComments,
    setShouldCheckClose,
    setShowCommentPreview,
    loadComments,
    task.id,
    startedLoad,
    setViewingTaskCommentsID,
  ]);

  const handleMouseLeave = React.useCallback(() => {
    if (hasModalWithComments) {
      return;
    }

    clearTimeout(hoverTime.current as NodeJS.Timeout);
    hoverTimeOut.current = setTimeout(() => {
      setShouldCheckClose && setShouldCheckClose(true);
    }, 250);
  }, [hasModalWithComments, setShouldCheckClose]);

  return (
    <div
      id={`task-comments-${task.id}`}
      className={clsx(
        classes.count,
        classes.hoverableIconContainer,
        showCommentPreview && classes.isViewing,
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <span className="count">{comments}</span>

      <span className="icon">
        <MessageText size={theme.size.sm} variant="Outline" />
      </span>
    </div>
  );
};

export default Comments;
